/* Marque (Paramètres › Marque) — the brand every customer screen wears: a logo and a colour. That is all.
   D-70 / the owner's note of 19/09: no roue settings outside the campaign builder — the roue is built at step 3
   (its gifts, their chances) and that is it. The look is derived: your colour on the wheel, your logo at its
   centre, an accessible text colour when yours is too light. Retired here: the « Style de roue » pane (themes,
   part count, second colour, logo toggle — D-1 / D-2 as merchant settings), and the per-roue style of D-20.
   Published campaigns keep their look until their next published version (CA-01).

   Migration v3.3 · WF-038 (« Identité par défaut »): the page gains the brand's two defaults for new campaigns —
   the « Phrase d’accueil » (the app's welcome sentence, lib/campaign TEXT_DEFAULTS 'entry.h', with the
   establishment's name) and the « Photo du salon » — the live preview of the customer welcome card (the builder's own
   WelcomeMini, never a second renderer), « Aperçu non enregistré » while the form differs from what is saved,
   « Revenir à l’identité BMR », the rule-13 table and the Paramètres strip. The image cap stays the app's 5 Mo (the
   reference's 500 Ko is its browser storage, ⟦confirmable⟧). The two defaults live in this file's own store (one key
   per world, this browser) until the Orchestrator carries them on the brand (lib/wheel WheelDesign / the API's
   brand), listed in the pack's HANDOFF § 0 — the builder's texts still start from TEXT_DEFAULTS until then. */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Field, Input, Note, Pill, useToast } from '@/ui'
import { ALL, useScope } from '@/lib/scope'
import { useLocations } from '@/lib/api'
import { currentWorld } from '@/mocks/worlds'
import { identityKey } from '@/routes/Accueil'
import { DEFAULT_DESIGN, Wheel, getWheelDesign, setWheelDesign, getRoues, type WheelDesign } from '@/lib/wheel'
import { TEXT_DEFAULTS, getAdresse, getFiche, layoutOf } from '@/lib/campaign'
import ImageDrop from '@/routes/campagnes/ImageDrop'
import WelcomeMini from '@/routes/campagnes/Welcome'
import { toastWhenSaved, writeMark } from '@/lib/writes'
import { EffetTable, ParamTabs, SETTINGS_READ_ONLY_LINE, canEditSettings } from '@/routes/parametres/ParamShell'

/* ---------- the brand's defaults for new campaigns (WF-038) — this browser, one key per world ---------- */
export interface BrandIdentity { welcome: string | null; photoUrl: string | null }
const identityStoreKey = () => `bmr.brand-identity.${currentWorld()}`
export function getBrandIdentity(): BrandIdentity {
  try { const raw = localStorage.getItem(identityStoreKey()); if (raw) { const v = JSON.parse(raw) as Partial<BrandIdentity>; return { welcome: typeof v.welcome === 'string' && v.welcome.trim() ? v.welcome : null, photoUrl: typeof v.photoUrl === 'string' ? v.photoUrl : null } } } catch { /* fine */ }
  return { welcome: null, photoUrl: null }
}
export function setBrandIdentity(v: BrandIdentity): void {
  try { if (!v.welcome && !v.photoUrl) localStorage.removeItem(identityStoreKey()); else localStorage.setItem(identityStoreKey(), JSON.stringify(v)) } catch { /* fine */ }
}
/** The app's welcome sentence with the establishment's name — the « Phrase d’accueil » when none is typed. */
export const defaultWelcome = (placeName: string) => TEXT_DEFAULTS['entry.h'].value.replace('{établissement}', placeName)

/** WCAG relative luminance → contrast vs white text, for the honest guard. */
function contrastOnWhite(hex: string): number {
  const n = parseInt(hex.slice(1), 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return (1 + 0.05) / (L + 0.05)
}

const logoNameOf = (d: WheelDesign) => d.logo ? (d.logoUrl ? 'votre logo' : 'logo-valessio.svg') : null

export default function Identite() {
  const saved = getWheelDesign()
  const savedIdentity = getBrandIdentity()
  const [colorA, setColorA] = useState(saved.a)
  const [logoName, setLogoName] = useState<string | null>(logoNameOf(saved))
  /* D-87 · the uploaded logo (POST /v1/app/media) — its url is what brand.logoUrl stores */
  const [logoUrl, setLogoUrl] = useState<string | null>(saved.logoUrl ?? null)
  /* WF-038 · the two defaults for new campaigns */
  const [welcome, setWelcome] = useState<string>(savedIdentity.welcome ?? '')
  const [photoUrl, setPhotoUrl] = useState<string | null>(savedIdentity.photoUrl)
  const roues = getRoues()
  const nav = useNavigate()
  const toast = useToast()
  const [scope] = useScope()
  const inCtx = scope !== ALL
  /* WF-126 (default, HANDOFF § 3): a Manager reads this page; the owner and an administrator write it */
  const readOnly = !canEditSettings()
  const { data: locations } = useLocations()
  const loc = (locations ?? []).find(l => l.status !== 'inactive') ?? (locations ?? [])[0]
  const placeName = getFiche()?.name ?? getAdresse()?.name ?? loc?.name ?? 'votre établissement'
  const markIdentityDone = () => { try { localStorage.setItem(identityKey(), '1') } catch { /* fine */ } }

  const ratio = useMemo(() => contrastOnWhite(colorA), [colorA])
  const readable = ratio >= 4.5
  const design: WheelDesign = { ...saved, a: colorA, logo: !!logoName, logoUrl: logoName ? logoUrl : null }
  const identity: BrandIdentity = { welcome: welcome.trim() || null, photoUrl }
  /* « Aperçu non enregistré »: the form differs from what is saved */
  const dirty = design.a !== saved.a || design.logo !== saved.logo || (design.logoUrl ?? null) !== (saved.logoUrl ?? null)
    || identity.welcome !== savedIdentity.welcome || identity.photoUrl !== savedIdentity.photoUrl
  const sentence = identity.welcome ?? defaultWelcome(placeName)

  const readBack = () => {
    const d = getWheelDesign(); setColorA(d.a); setLogoName(logoNameOf(d)); setLogoUrl(d.logoUrl ?? null)
    const i = getBrandIdentity(); setWelcome(i.welcome ?? ''); setPhotoUrl(i.photoUrl)
  }
  const save = () => {
    const mark = writeMark()
    setWheelDesign(design); setBrandIdentity(identity); markIdentityDone()
    /* 2b-2 · with an API the « ✓ » waits for the server; refused, the page shows the brand the server holds */
    toastWhenSaved(mark, toast, { message: '✓ Marque enregistrée — elle habille chaque écran client.', open: { label: 'Vos campagnes', go: () => nav('/campagnes') } }, readBack)
  }
  /* « Revenir à l’identité BMR »: the app's defaults, in the form — saved only by « Enregistrer » */
  const reset = () => { setColorA(DEFAULT_DESIGN.a); setLogoName(null); setLogoUrl(null); setWelcome(''); setPhotoUrl(null) }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 980 }} data-testid="marque">
      {/* v3.3 · WF-038: the Paramètres strip — Marque is its second tab */}
      <ParamTabs />
      <div>
        <h2 className="t-page" style={{ margin: 0 }}>Marque</h2>
        <p className="f-help" style={{ marginTop: 6 }}>
          Votre logo et votre couleur, sur chaque écran client — l’entrée, la roue, le cadeau. Réglés une fois.
          {' '}Les roues elles-mêmes — cadeaux et chances — se construisent dans la campagne, à l’étape 3.
        </p>
        {inCtx && <Note tone="candour">La marque est celle du workspace — la même dans tous vos établissements.</Note>}
        {readOnly && <p className="f-help" data-testid="lecture-seule" style={{ margin: '6px 0 0' }}>{SETTINGS_READ_ONLY_LINE}</p>}
      </div>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', alignItems: 'start' }}>
        <Card data-testid="identite-defaut">
          <p className="t-eyebrow" style={{ margin: '0 0 12px' }}>Identité par défaut</p>
          <div className="fields-row" style={{ gridTemplateColumns: '1fr' }}>
            {/* WF-038 · the welcome sentence new campaigns start from; the app's own when nothing is typed */}
            <Field label="Phrase d’accueil" help="Le titre de l’entrée de vos nouvelles campagnes. Vide, c’est la phrase BMR, avec le nom de votre établissement.">
              {id => <Input id={id} value={welcome} disabled={readOnly} maxLength={120} onChange={e => setWelcome(e.target.value)} placeholder={defaultWelcome(placeName)} data-testid="phrase-accueil" />}
            </Field>
            <Field label="Couleur principale" help={readable ? `Contraste ${ratio.toFixed(1)}:1 — lisible sur les boutons.` : `Texte blanc illisible sur cette couleur (${ratio.toFixed(1)}:1) : elle restera en décor, les textes passeront sur une version foncée.`}>
              {id => <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input id={id} type="color" value={colorA} disabled={readOnly} onChange={e => setColorA(e.target.value)} aria-label="Couleur principale" style={{ width: 44, height: 34, border: '1px solid var(--bmr-border)', borderRadius: 8, background: 'none' }} /><Input value={colorA} disabled={readOnly} onChange={e => /^#[0-9a-fA-F]{6}$/.test(e.target.value) && setColorA(e.target.value)} style={{ width: 120 }} aria-label="Code couleur" /></div>}
            </Field>
            <Field label="Logo" help="JPEG, PNG ou WebP, 5 Mo au plus — au centre de la roue et en haut de chaque écran.">{id => (
              <div id={id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }} data-testid="marque-logo">
                {logoUrl && logoName && <img src={logoUrl} alt="Votre logo" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--bmr-border)', background: 'var(--bmr-surface)' }} />}
                {!readOnly && <ImageDrop label="Déposez votre logo" testid="marque-logo-drop" onUploaded={(url, name) => { setLogoUrl(url); setLogoName(name); toast({ message: `✓ Logo déposé — ${name}.` }) }}>
                  {logoName ? `${logoName} · remplacer` : 'Déposez votre logo'}
                </ImageDrop>}
                {readOnly && <span className="f-help">{logoName ?? 'aucun logo'}</span>}
                {logoName && !readOnly && <Button size="sm" variant="quiet" onClick={() => { setLogoName(null); setLogoUrl(null) }}>Retirer le logo</Button>}
              </div>
            )}</Field>
            {/* WF-038 · the salon's photo: the welcome band of new campaigns (a campaign may pick its own at step 2) */}
            <Field label="Photo du salon" help="JPEG, PNG ou WebP, 5 Mo au plus — le bandeau de l’accueil de vos nouvelles campagnes. Les campagnes publiées restent figées.">{id => (
              <div id={id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }} data-testid="marque-photo">
                {photoUrl && <img src={photoUrl} alt="Photo du salon" style={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--bmr-border)' }} />}
                {!readOnly && <ImageDrop label="Déposez une photo" testid="marque-photo-drop" onUploaded={(url, name) => { setPhotoUrl(url); toast({ message: `✓ Photo déposée — ${name}.` }) }}>
                  {photoUrl ? 'votre photo · remplacer' : 'Déposez une photo'}
                </ImageDrop>}
                {readOnly && <span className="f-help">{photoUrl ? 'votre photo' : 'aucune photo'}</span>}
                {photoUrl && !readOnly && <Button size="sm" variant="quiet" onClick={() => setPhotoUrl(null)}>Retirer la photo</Button>}
              </div>
            )}</Field>
          </div>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
              <Button variant="primary" onClick={save}>Enregistrer</Button>
              <Button variant="quiet" onClick={() => { setWheelDesign(design); setBrandIdentity(identity); window.open('/d/demo?etape=spin', '_blank', 'noopener') }}>Voir sur le parcours</Button>
              <Button variant="quiet" onClick={reset} data-testid="revenir-bmr">Revenir à l’identité BMR</Button>
            </div>
          )}
          <p className="f-help" style={{ margin: '12px 0 0' }}>Ces réglages servent de point de départ aux nouvelles campagnes. Une campagne peut personnaliser sa phrase et sa présentation. Les images gardent leurs proportions&nbsp;; Enregistrer modifie les réglages du salon, jamais les cadeaux déjà gagnés.</p>
        </Card>
        <Card data-testid="apercu-direct">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 12px' }}>
            <p className="t-eyebrow" style={{ margin: 0 }}>Ce que voit la cliente</p>
            {dirty ? <Pill tone="warn">Aperçu non enregistré</Pill> : <Pill tone="ok">Aperçu en direct</Pill>}
          </div>
          <div style={{ display: 'grid', placeItems: 'center', gap: 14 }}>
            {/* the builder's own welcome card — the brand's defaults as a new campaign would start */}
            <WelcomeMini width={240} layout={layoutOf({ photo: photoUrl }, colorA)} colour={colorA} logo={design.logo} logoUrl={design.logoUrl ?? null} photo={photoUrl}
              sentence={sentence} promise={TEXT_DEFAULTS['entry.p'].value} brand={placeName} design={design} />
            <Wheel design={design} size={150} />
          </div>
          <p className="f-help" style={{ textAlign: 'center', marginTop: 12 }}>
            {roues.length === 0 ? 'Votre couleur et votre logo habilleront chaque roue que vous construirez.' : roues.length === 1 ? `Votre roue « ${roues[0].name} » les porte déjà.` : `Vos ${roues.length} roues les portent déjà.`}
            {' '}Aperçu de l’accueil · le test complet se fait depuis la campagne.
          </p>
        </Card>
      </div>
      <Note tone="candour">Les campagnes déjà publiées gardent leur look&nbsp;; vos changements s’appliquent à la prochaine version publiée.</Note>
      {/* WF-038 · rule 13: when a change takes effect */}
      <EffetTable />
    </div>
  )
}
